import { Component, OnInit } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TenantService } from '../../core/services/tenant.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SortConfig } from '../../core/interface/SortConfig';
import { PaginationConfig } from '../../core/interface/PaginationConfig';
import { TenantList } from '../../core/models/tenant';

@Component({
  selector: 'app-tenant-list',
  templateUrl: './tenant-list.component.html',
  styleUrl: './tenant-list.component.css'
})
export class TenantListComponent implements OnInit {

  itemTenantNonGroup: TenantList[] = []
  itemTenantGroup: TenantList[] = []
  
  isLoadingGroup: boolean = false;
  isLoadingMoreGroup: boolean = false; // Loading state khusus untuk show more
  isLoadingNonGroup: boolean = false;
  isLoadingMoreNonGroup: boolean = false; // Loading state khusus untuk show more

  searchTerm: string = '';
  startDate: string = '';
  endDate: string = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();
  isShowMoreGroup: boolean = false
  isShowMoreNonGroup: boolean = false

  sortConfig: SortConfig = {
    column: '',
    direction: ''
  };

  paginationConfig: PaginationConfig = {
    currentPage: 1,
    itemsPerPage: 8,
    totalItems: 0
  };

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private tenantService: TenantService
  ) {
  }

  private setupSearchSubscriptionGroup(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Reset pagination saat search
      this.paginationConfig.currentPage = 1;
      this.itemTenantGroup = []; // Clear existing data

      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadTenantListGroup(term);
    });
  }
  private setupSearchSubscriptionNonGroup(): void {
    this.searchSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      // Reset pagination saat search
      this.paginationConfig.currentPage = 1;
      this.itemTenantNonGroup = []; // Clear existing data

      if (term.trim()) {
        this.updateUrlParams();
      }
      this.loadTenantListNonGroup(term);
    });
  }

  ngOnInit(): void {
    const startDate = localStorage.getItem('startDate')
    const endDate = localStorage.getItem('endDate')

    this.startDate = startDate ? startDate : '';
    this.endDate = endDate ? endDate : '';

    this.setupSearchSubscriptionGroup();
    this.setupSearchSubscriptionNonGroup();
    this.loadTenantListGroup();
    this.loadTenantListNonGroup();
  }

  loadTenantListGroup(searchTerm?: string, isLoadMore: boolean = false): void {
    if (isLoadMore) {
      this.isLoadingMoreGroup = true;
    } else {
      this.isLoadingGroup = true;
    }

    this.tenantService.getTenantListGroup(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        if (isLoadMore) {
          // Append data baru ke data yang sudah ada
          this.itemTenantGroup = [...this.itemTenantGroup, ...response.data];
          this.isLoadingMoreGroup = false;
        } else {
          // Replace data (untuk initial load atau search)
          this.itemTenantGroup = response.data;
          this.isLoadingGroup = false;
        }

        this.paginationConfig.totalItems = response.pagination.total_items;

        // Check apakah masih ada data untuk di-load
        this.updateShowMoreVisibilityGroup();

        console.log(this.itemTenantGroup);
      },
      error: (error) => {
        console.error('Error loading tenant data:', error);
        this.isLoadingGroup = false;
        this.isLoadingMoreGroup = false;
        if (!isLoadMore) {
          this.itemTenantGroup= [];
        }
      }
    });
  }
  
  loadTenantListNonGroup(searchTerm?: string, isLoadMore: boolean = false): void {
    if (isLoadMore) {
      this.isLoadingMoreNonGroup = true;
    } else {
      this.isLoadingNonGroup = true;
    }

    this.tenantService.getTenantListNonGroup(
      this.paginationConfig.currentPage,
      this.paginationConfig.itemsPerPage,
      searchTerm?.trim() || '',
      this.startDate,
      this.endDate,
    ).subscribe({
      next: (response) => {
        if (isLoadMore) {
          // Append data baru ke data yang sudah ada
          this.itemTenantNonGroup = [...this.itemTenantNonGroup, ...response.data];
          this.isLoadingMoreNonGroup = false;
        } else {
          // Replace data (untuk initial load atau search)
          this.itemTenantNonGroup = response.data;
          this.isLoadingNonGroup = false;
        }

        this.paginationConfig.totalItems = response.pagination.total_items;

        // Check apakah masih ada data untuk di-load
        this.updateShowMoreVisibilityNonGroup();

        console.log(this.itemTenantNonGroup);
      },
      error: (error) => {
        console.error('Error loading tenant data:', error);
        this.isLoadingNonGroup = false;
        this.isLoadingMoreNonGroup = false;
        if (!isLoadMore) {
          this.itemTenantNonGroup = [];
        }
      }
    });
  }

  onClickMoreGroup(): void {
    if (this.isLoadingMoreGroup) return; // Prevent multiple clicks

    // Increment page untuk load data selanjutnya
    this.paginationConfig.currentPage++;

    // Load data dengan flag isLoadMore = true
    this.loadTenantListGroup(this.searchTerm, true);
  }

  onClickMoreNonGroup(): void {
    if (this.isLoadingMoreNonGroup) return; // Prevent multiple clicks

    // Increment page untuk load data selanjutnya
    this.paginationConfig.currentPage++;

    // Load data dengan flag isLoadMore = true
    this.loadTenantListNonGroup(this.searchTerm, true);
  }

  private updateShowMoreVisibilityNonGroup(): void {
    // Hitung total item yang sudah di-load
    const loadedItems = this.itemTenantNonGroup.length;

    // Show more button jika masih ada data yang belum di-load
    this.isShowMoreNonGroup = loadedItems < this.paginationConfig.totalItems;
  }

  private updateShowMoreVisibilityGroup(): void {
    // Hitung total item yang sudah di-load
    const loadedItems = this.itemTenantGroup.length;

    // Show more button jika masih ada data yang belum di-load
    this.isShowMoreGroup = loadedItems < this.paginationConfig.totalItems;
  }

  // Helper method untuk reset data (bisa digunakan saat search atau filter)
  private resetPagination(): void {
    this.paginationConfig.currentPage = 1;
    this.itemTenantNonGroup = [];
  }

  // Method untuk handle search (jika Anda ingin menggunakan search functionality)
  onSearch(searchTerm: string): void {
    this.searchTerm = searchTerm;
    this.resetPagination();
    this.searchSubject.next(searchTerm);
  }

  // Method untuk handle search event dari input
  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.onSearch(target.value);
  }

  private updateUrlParams(): void {
    const queryParams: any = {
      page: this.paginationConfig.currentPage
    };

    // Hanya tambahkan search param jika ada nilai
    if (this.searchTerm?.trim()) {
      queryParams.search = this.searchTerm.trim();
    } else {
      queryParams.search = null; // Hapus search param
    }

    if (this.sortConfig.column && this.sortConfig.direction) {
      queryParams.sortColumn = this.sortConfig.column;
      queryParams.sortDirection = this.sortConfig.direction;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }
}
